import { iAppConfig } from "../types/common";

const TRUTHY = new Set(["true", "1", "yes", "on", "enabled"]);

/**
 * Whether a boolean config-table flag (exposed to the FE via `appConfig`) is on.
 * Driven ONLY by the row's `value` (matches the app-wide `?.value === "true"`
 * convention). Do NOT consult `row.status` — that's the config row's generic active
 * flag, not the toggle. Absent / non-truthy value => off.
 */
export const isConfigFlagEnabled = (appConfig: iAppConfig[] | undefined | null, key: string): boolean => {
  if (!Array.isArray(appConfig)) return false;
  const row = appConfig.find((item) => item?.key === key);
  if (!row) return false;
  const value = String(row.value ?? "").trim().toLowerCase();
  return TRUTHY.has(value);
};

/**
 * The row's normalised value, or `undefined` when there is NO ROW.
 *
 * `isConfigFlagEnabled` above collapses "no row" and "row says false" into the
 * same `false`, which is right for a flag that ships off and is switched on.
 * It is wrong for a flag that gates something ALREADY LIVE: there, "no row" must
 * mean "leave it as it is", not "turn it off".
 *
 * `appConfig` is also `undefined` for the first render, before the config fetch
 * resolves — indistinguishable from "no row" here, and deliberately so: both
 * mean "no answer yet", and the caller's default is the right response to both.
 */
export const configFlagValue = (
  appConfig: iAppConfig[] | undefined | null,
  key: string,
): string | undefined => {
  if (!Array.isArray(appConfig)) return undefined;
  const row = appConfig.find((item) => item?.key === key);
  if (!row) return undefined;
  return String(row.value ?? "").trim().toLowerCase();
};

/**
 * Like `isConfigFlagEnabled`, but you choose what "no row" means.
 *
 * Pass `whenAbsent: true` for a flag whose feature is already shipped and
 * visible, so that neither a missing row nor a failed config fetch can silently
 * withdraw it. Pass `false` (or just use `isConfigFlagEnabled`) for a flag
 * rolling something out.
 */
export const isConfigFlagEnabledWithDefault = (
  appConfig: iAppConfig[] | undefined | null,
  key: string,
  whenAbsent: boolean,
): boolean => {
  const value = configFlagValue(appConfig, key);
  if (value === undefined) return whenAbsent;
  return TRUTHY.has(value);
};
